import { takeLatest, put, all, delay, call, takeEvery, select } from "redux-saga/effects";
import { normalize, schema } from "normalizr";
import {
    INITIAL_SCRAPE_TASK_BATCH_STARTS_FILTER_OPTIONS,
    INITIAL_SCRAPE_TASK_BATCH_STARTS_ITEMS_PER_PAGE
} from "modules/InitialScrapeTaskBatchStarts/constants";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { callWithNetworkRetry } from "utils/sagasRetry";
import {
    getDetailedProgressRequest,
    getInitialScrapeTaskBatchStartsRequest,
    runInitialScrapeTaskBatchStartRequest
} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/api";
import {
    getInitialScrapeTaskBatchStarts,
    getInitialScrapeTaskBatchStartsSuccess,
    getInitialScrapeTaskBatchStartsError,
    mergeInitialScrapeTaskBatchStartDialog,
    setInitialScrapeTaskBatchStartDialog,
    getDetailedProgressInfo,
    setDetailedProgressInfo,
} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/actions";
import {initialScrapeTaskBatchStart} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/actions";
import {toast} from "react-toastify";
import queryString from "query-string";
import {exportResultObjects, mergeDownloadResultObjectsDialog} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/actions";
import {exportScrapedResultsRequest, pidProcessRequest} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/api";
import {
    downloadDialogSelector,
    initialScrapeTaskBatchStartsEntriesSelector
} from "modules/InitialScrapeTaskBatchStarts/services/InitialScrapeTaskBatchStarts/selectors";
import {getUsersWorker} from "../../../../services/Users/sagas";
import {getBranchesWorker} from "../../../../services/Branch/sagas";
import {getBrandsWorker} from "../../../../services/Brand/sagas";

const scrapeInitialScrapeTaskBatchStartsSchema = new schema.Entity("initial_scrape_task_batch_starts");
const scrapeInitialScrapeTaskBatchStartsListSchema = new schema.Array(scrapeInitialScrapeTaskBatchStartsSchema);

//Workers
function* handleRefreshInitialScrapeTaskBatchStarts() {
    const { search } = window.location;
    yield put(getInitialScrapeTaskBatchStarts({ limit: INITIAL_SCRAPE_TASK_BATCH_STARTS_ITEMS_PER_PAGE, ...queryString.parse(search) }));
}

function* handleGetInitialScrapeTaskBatchStarts ({ payload }) {
    try {
        let searchDefault = {
            ...INITIAL_SCRAPE_TASK_BATCH_STARTS_FILTER_OPTIONS,
            ...payload
        };
        const { initial_scrape_task_batch_starts, count } = yield call(getInitialScrapeTaskBatchStartsRequest, searchDefault);
        const { result, entities } = normalize(initial_scrape_task_batch_starts, scrapeInitialScrapeTaskBatchStartsListSchema);
        yield put(getInitialScrapeTaskBatchStartsSuccess({
            initialScrapeTaskBatchStarts: entities.initial_scrape_task_batch_starts,
            ids: result,
            count
        }));
    } catch (error) {
        yield put(getInitialScrapeTaskBatchStartsError());
        getErrorMessage(error);
    }
}

function* handleInitialScrapeTaskBatchStartRun ({ payload }) {
    try {
        yield put(mergeInitialScrapeTaskBatchStartDialog({ isLoading: true }));
        const { batch_id } = yield call(runInitialScrapeTaskBatchStartRequest, payload);
        yield put(setInitialScrapeTaskBatchStartDialog());
        yield call(
            toast.success,
            `Initial scrape task batch start with id ${batch_id} has been successfully created`
        );
        yield handleRefreshInitialScrapeTaskBatchStarts();
    } catch (error) {
        yield put(mergeInitialScrapeTaskBatchStartDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

const NETWORK_RETRY_TOAST_ID = "export-result-objects-network-retry";

const notifyNetworkRetry = () => {
    if (!toast.isActive(NETWORK_RETRY_TOAST_ID)) {
        toast.warning("Connection lost. Retrying to get export progress...", { toastId: NETWORK_RETRY_TOAST_ID });
    }
};

function* getAllResultObjects (pid) {
    try {
        while (true) {
            const { isOpen } = yield select(downloadDialogSelector);

            const value = yield callWithNetworkRetry(pidProcessRequest, [pid], { onRetry: notifyNetworkRetry });
            toast.dismiss(NETWORK_RETRY_TOAST_ID);
            const status = Number((value * 100).toFixed());
            yield put(mergeDownloadResultObjectsDialog({ isLoading: true, status }));
            if (status >= 99) {
                yield delay(4000);
            }
            if (status === 100 || !isOpen) {
                break;
            }
            yield delay(1500);
        }
        yield put(mergeDownloadResultObjectsDialog({ isLoading: false, status: null }));
    } catch (error) {
        toast.dismiss(NETWORK_RETRY_TOAST_ID);
        getErrorMessage(error);
        yield put(mergeDownloadResultObjectsDialog({ isLoading: false }));
    }
}

function* handleExportResultObjects ({ payload }) {
    try {
        yield put(mergeDownloadResultObjectsDialog({ isOpen: true, isLoading: true, status: 0, url: "" }));
        const { pid, url } = yield call(exportScrapedResultsRequest, payload);
        yield put(mergeDownloadResultObjectsDialog({ isOpen: true, isLoading: true, status: 0, url }));
        yield getAllResultObjects(pid, url);
    } catch (error) {
        getErrorMessage(error);
    }
}

export function* handleGetDetailedProgressInfo({ payload }) {
    try {
        const { progressInfo } = yield select(initialScrapeTaskBatchStartsEntriesSelector);
        const newProgressInfo = { ...progressInfo};
        newProgressInfo[payload.batch_id] = yield call(creatingNewDetailedProgressWorker, payload);
        yield put(setDetailedProgressInfo(newProgressInfo));
    } catch (error) {
        getErrorMessage(error);
    }
}

export function* creatingNewDetailedProgressWorker(data) {
    try {
        const { batch_id } = data;
        return yield call(getDetailedProgressRequest, batch_id);
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* initialScrapeTaskBatchStartsEntriesSagas() {
    yield all([
        call(getUsersWorker),
        call(getBranchesWorker),
        call(getBrandsWorker),
        takeLatest(getInitialScrapeTaskBatchStarts, handleGetInitialScrapeTaskBatchStarts),
        takeLatest(initialScrapeTaskBatchStart, handleInitialScrapeTaskBatchStartRun),
        takeLatest(exportResultObjects, handleExportResultObjects),
        takeEvery(getDetailedProgressInfo, handleGetDetailedProgressInfo),
    ]);
}
